import type { ReactNode } from 'react'

/** Classe das células das tabelas de lista. */
export const TD_CLASS = 'whitespace-nowrap px-4 py-3 align-middle text-[13px] text-body'

/**
 * Esqueleto das tabelas de lista do Atendimento HUB e de Ocorrências: cartão
 * branco arredondado, cabeçalho em caixa-alta, linhas separadas e rodapé
 * (resumo + paginação). Quem usa monta as `<th>` (`SortableTh`/`TH_CLASS`) e
 * as linhas (`ClickableRow`).
 */
export function ListTable({
  head,
  children,
  footer,
}: {
  head: ReactNode
  children: ReactNode
  /** Rodapé do cartão — normalmente `TablePagination`. */
  footer?: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle text-left text-[11px] uppercase tracking-[0.06em] text-subtle">
              {head}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">{children}</tbody>
        </table>
      </div>
      {footer}
    </div>
  )
}

/**
 * Linha inteira clicável. Não é `<button>`: uma linha pode conter controles
 * (ex.: select de prioridade do HUB), e HTML não permite interativo dentro de
 * interativo — daí `role="button"` + teclado.
 */
export function ClickableRow({
  onClick,
  children,
  highlighted = false,
}: {
  onClick: () => void
  children: ReactNode
  /** Fundo de destaque (ex.: linha expandida). */
  highlighted?: boolean
}) {
  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        // Só teclas na PRÓPRIA linha: Enter/Espaço num controle dentro dela
        // (ex.: select de prioridade do HUB) não pode abrir a linha.
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={`cursor-pointer transition hover:bg-surface-muted focus:bg-surface-muted focus:outline-none ${
        highlighted ? 'bg-canvas/60' : ''
      }`}
    >
      {children}
    </tr>
  )
}
